import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  InputAdornment,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Public as PublicIcon,
  Title as TitleIcon,
  Lock as LockIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  SwapHoriz as ReplaceIcon,
  InsertDriveFile as FileIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axios';
import { useAuth } from '../../../contexts/AuthContext';
import { format } from 'date-fns';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmUpdateDialog from '../../common/ConfirmDeleteDialog';
import {
  applyApiValidationErrors,
  createFieldErrorGate,
  formatFieldErrorsMessage,
  getFirstErrorMessage,
  rejectConfirmValidation,
} from '../../../utils/formValidationFeedback';
import CommonSectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import { SUCCESS } from '../../common/SaaSTokens';
import { canManageDocuments } from '../../../utils/documentPermissions';
import { buildPdfEmbedSrc, fetchDocumentPreviewBlob } from '../../../utils/protectedFilePreview';
import SearchableSelect from '../../common/SearchableSelect';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const B = { 600: '#2563EB', 700: '#1D4ED8', 800: '#1E40AF', 900: '#1E3A8A' };
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

const TEXT_BODY = '#E2E8F0';
const TEXT_MUTED = '#94A3B8';

const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-pulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes form-glow-in { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  @keyframes neon-ping    { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }
  @keyframes drop-zone-pulse { 0%,100% { border-color: rgba(56,189,248,0.6); box-shadow: 0 0 0 0 rgba(56,189,248,0.2); } 50% { border-color: rgba(56,189,248,0.9); box-shadow: 0 0 20px 4px rgba(56,189,248,0.15); } }
  @keyframes file-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

  .MuiOutlinedInput-input,
  .MuiInputBase-input {
    color: #E2E8F0 !important;
    -webkit-text-fill-color: #E2E8F0 !important;
    caret-color: #38BDF8 !important;
  }
  .MuiOutlinedInput-input:-webkit-autofill,
  .MuiInputBase-input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 100px #0f1f38 inset !important;
    -webkit-text-fill-color: #E2E8F0 !important;
  }
`;

const SectionCard = ({ title, icon, delay = 0, children }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay}>
    {children}
  </CommonSectionCard>
);

// ─── Shared input sx ───────────────────────────────────────────────────────────
const inputRootSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    color: alpha(TEXT_BODY, 0.85),
    background: alpha(SURFACE_UP, 0.7),
    backdropFilter: 'blur(8px)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': { borderColor: alpha(NEON, 0.18), transition: 'border-color 0.2s ease' },
    '&:hover fieldset': { borderColor: alpha(NEON, 0.4) },
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}`, animation: 'form-glow-in 0.3s ease' },
    '&.Mui-focused fieldset': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
  },
  '& .MuiInputBase-input': {
    color: `${alpha(TEXT_BODY, 0.85)} !important`,
    WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important`,
    '&::placeholder': { color: alpha(NEON, 0.3), opacity: 1 },
  },
  '& .MuiInputLabel-root': { fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45) },
  '& label.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 },
  '& .MuiFormLabel-asterisk': { color: '#EF4444', fontWeight: 700 },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: alpha(NEON, 0.45), fontSize: 18 },
  '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
  '& .MuiFormHelperText-root': { fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: alpha(NEON, 0.5) },
  '& .MuiFormHelperText-root.Mui-error': { color: '#F87171' },
  '& .MuiOutlinedInput-root .MuiSelect-select': { color: `${alpha(TEXT_BODY, 0.85)} !important`, WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important` },
};

const StyledField = ({ sx, ...props }) => (
  <TextField {...props} sx={{ ...inputRootSx, ...sx }} />
);

const StyledSelect = ({ label, value, onChange, children, required, startIcon, helperText, error, sx }) => (
  <FormControl fullWidth required={required} error={error} sx={{ flex: 1, ...inputRootSx, ...sx }}>
    <InputLabel sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45), '&.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 } }}>
      {label}
    </InputLabel>
    <Select
      value={value}
      onChange={onChange}
      label={label}
      startAdornment={startIcon ? <InputAdornment position="start">{startIcon}</InputAdornment> : undefined}
      MenuProps={{
        PaperProps: {
          sx: {
            background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.98)} 0%, ${alpha(SURFACE_MID, 0.96)} 100%)`,
            border: `1px solid ${alpha(NEON, 0.2)}`,
            boxShadow: `0 8px 32px ${alpha('#000', 0.6)}, inset 0 1px 0 ${alpha(NEON, 0.1)}`,
            backdropFilter: 'blur(12px)',
            '& .MuiMenuItem-root': {
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: alpha(TEXT_BODY, 0.85),
              transition: 'all 0.15s ease',
              '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.12)}, ${alpha(B[700], 0.08)})`, color: NEON },
              '&.Mui-selected': {
                background: `linear-gradient(135deg, ${alpha(NEON, 0.18)}, ${alpha(B[700], 0.12)})`, color: NEON, fontWeight: 600,
                '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.22)}, ${alpha(B[700], 0.15)})` },
              },
            },
          },
        },
      }}
      sx={{
        borderRadius: '10px', fontFamily: "'DM Sans', sans-serif",
        color: alpha(TEXT_BODY, 0.85), background: alpha(SURFACE_UP, 0.7), backdropFilter: 'blur(8px)',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.18) },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.4) },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}` },
        '& .MuiSelect-select': { color: `${alpha(TEXT_BODY, 0.85)} !important`, WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important` },
        '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
      }}
    >
      {children}
    </Select>
    {helperText && (
      <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: error ? '#F87171' : alpha(NEON, 0.5), mt: 0.4, mx: 1.75 }}>
        {helperText}
      </Typography>
    )}
  </FormControl>
);

const DOCUMENT_VALIDATION_FIELDS = new Set([
  'event_id', 'document_name', 'document_pdf', 'publicity', 'user_ids',
]);
const formatBytes = (bytes) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

// ─── DocumentUploadZone ────────────────────────────────────────────────────────
const DocumentUploadZone = ({
  formData, filePreviewUrl, isEditMode, documentData,
  documentPdfError, fileInputRef, onFileChange, onFileDrop, onRemoveFile,
}) => {
  const [isDragOver, setIsDragOver]       = useState(false);
  const [previewOpen, setPreviewOpen]     = useState(false);

  const hasNewFile     = Boolean(formData.document_pdf);
  const hasExisting    = isEditMode && documentData?.document_pdf && !hasNewFile;
  const hasAnyFile     = hasNewFile || hasExisting;

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e) => { setIsDragOver(false); onFileDrop(e); };

  // ── State: no file (dropzone) ───────────────────────────────────────────────
  if (!hasAnyFile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            position: 'relative', overflow: 'hidden',
            p: { xs: 4, md: 5 }, textAlign: 'center',
            borderRadius: '14px', cursor: 'pointer',
            border: `2px dashed ${isDragOver ? NEON : alpha(NEON, 0.3)}`,
            background: isDragOver
              ? `radial-gradient(ellipse at center, ${alpha(NEON, 0.07)} 0%, ${alpha(SURFACE_UP, 0.6)} 70%)`
              : alpha(SURFACE_UP, 0.35),
            transition: 'all 0.25s ease',
            animation: isDragOver ? 'drop-zone-pulse 1s ease infinite' : 'none',
            '&:hover': {
              background: `radial-gradient(ellipse at center, ${alpha(NEON, 0.06)} 0%, ${alpha(SURFACE_UP, 0.5)} 70%)`,
              borderColor: alpha(NEON, 0.55),
              boxShadow: `0 0 24px ${alpha(NEON, 0.1)}`,
            },
          }}
        >
          {/* Corner accents */}
          {[['0','0','border-top','border-left'],['0','auto','border-top','border-right'],['auto','0','border-bottom','border-left'],['auto','auto','border-bottom','border-right']].map(([t,r,bv,bh], i) => (
            <Box key={i} sx={{
              position: 'absolute', top: t !== 'auto' ? 10 : 'auto', right: r !== 'auto' ? (r === '0' ? 10 : 10) : 'auto',
              bottom: t === 'auto' ? 10 : 'auto', left: r === 'auto' && t !== 'auto' ? 'auto' : (r === 'auto' ? 10 : 'auto'),
              width: 14, height: 14,
              borderTop: bv === 'border-top' ? `2px solid ${alpha(NEON, isDragOver ? 0.9 : 0.4)}` : 'none',
              borderBottom: bv === 'border-bottom' ? `2px solid ${alpha(NEON, isDragOver ? 0.9 : 0.4)}` : 'none',
              borderLeft: bh === 'border-left' ? `2px solid ${alpha(NEON, isDragOver ? 0.9 : 0.4)}` : 'none',
              borderRight: bh === 'border-right' ? `2px solid ${alpha(NEON, isDragOver ? 0.9 : 0.4)}` : 'none',
              transition: 'border-color 0.25s ease',
            }} />
          ))}

          {/* Icon */}
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 64, height: 64, borderRadius: '16px', mb: 2.5,
            background: isDragOver
              ? `linear-gradient(135deg, ${alpha(NEON, 0.2)}, ${alpha(B[700], 0.3)})`
              : `linear-gradient(135deg, ${alpha(NEON, 0.1)}, ${alpha(B[700], 0.15)})`,
            border: `1px solid ${alpha(NEON, isDragOver ? 0.5 : 0.2)}`,
            boxShadow: isDragOver ? `0 0 20px ${alpha(NEON, 0.25)}` : 'none',
            transition: 'all 0.25s ease',
          }}>
            <UploadIcon sx={{ fontSize: 28, color: isDragOver ? NEON : alpha(NEON, 0.7) }} />
          </Box>

          {/* Text */}
          <Typography sx={{
            fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
            fontSize: { xs: '1rem', md: '1.1rem' },
            color: isDragOver ? NEON : alpha(TEXT_BODY, 0.9), mb: 0.75,
            transition: 'color 0.2s ease',
          }}>
            {isDragOver ? 'Drop your PDF here' : 'Drop PDF here or click to browse'}
            {!isEditMode && <Box component="span" sx={{ color: '#EF4444', ml: 0.5 }}>*</Box>}
          </Typography>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.83rem', color: alpha(TEXT_MUTED, 0.8), mb: 1 }}>
            Supports PDF files only
          </Typography>

          {/* Constraint pill */}
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            px: 1.5, py: 0.4, borderRadius: '20px',
            background: alpha(SURFACE_MID, 0.8), border: `1px solid ${alpha(NEON, 0.12)}`,
          }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: alpha(NEON, 0.5) }} />
            <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.7), letterSpacing: 0.5 }}>
              MAX 20 MB
            </Typography>
          </Box>
        </Box>

        {/* Validation error */}
        {documentPdfError && (
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', color: '#F87171', letterSpacing: 0.3, pl: 0.5 }}>
            ⚠ {documentPdfError}
          </Typography>
        )}

        <input ref={fileInputRef} type="file" accept=".pdf" onChange={onFileChange} style={{ display: 'none' }} />
      </Box>
    );
  }

  // ── State: file selected (new upload) ───────────────────────────────────────
  if (hasNewFile) {
    const file = formData.document_pdf;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, animation: 'file-in 0.3s ease' }}>

        {/* File card */}
        <Box sx={{
          borderRadius: '12px', overflow: 'hidden',
          border: `1px solid ${alpha(NEON3, 0.3)}`,
          background: `linear-gradient(135deg, ${alpha(NEON3, 0.05)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
        }}>
          {/* Card header row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
            {/* PDF icon block */}
            <Box sx={{
              flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 48, height: 48, borderRadius: '10px',
              background: `linear-gradient(135deg, ${alpha('#F87171', 0.2)}, ${alpha('#F87171', 0.06)})`,
              border: `1px solid ${alpha('#F87171', 0.3)}`,
              boxShadow: `0 0 12px ${alpha('#F87171', 0.12)}`,
            }}>
              <PdfIcon sx={{ fontSize: 22, color: '#F87171' }} />
            </Box>

            {/* File meta */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                <CheckCircleIcon sx={{ fontSize: 14, color: NEON3, flexShrink: 0 }} />
                <Typography sx={{
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
                  fontSize: '0.88rem', color: TEXT_BODY,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {file.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem', color: alpha(TEXT_MUTED, 0.8) }}>
                  {formatBytes(file.size)}
                </Typography>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: alpha(TEXT_MUTED, 0.4) }} />
                <Box sx={{
                  px: 1, py: 0.2, borderRadius: '4px',
                  background: alpha('#F87171', 0.1), border: `1px solid ${alpha('#F87171', 0.25)}`,
                }}>
                  <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6rem', color: '#F87171', fontWeight: 600, letterSpacing: 0.5 }}>
                    PDF
                  </Typography>
                </Box>
                <Box sx={{
                  px: 1, py: 0.2, borderRadius: '4px',
                  background: alpha(NEON3, 0.1), border: `1px solid ${alpha(NEON3, 0.25)}`,
                }}>
                  <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6rem', color: NEON3, fontWeight: 600, letterSpacing: 0.5 }}>
                    READY
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
              <Tooltip_btn label="Preview" onClick={() => setPreviewOpen(v => !v)}>
                {previewOpen ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
              </Tooltip_btn>
              <Tooltip_btn label="Replace file" onClick={() => fileInputRef.current?.click()} color={NEON}>
                <ReplaceIcon sx={{ fontSize: 16 }} />
              </Tooltip_btn>
              <Tooltip_btn label="Remove" onClick={onRemoveFile} color="#F87171">
                <DeleteIcon sx={{ fontSize: 16 }} />
              </Tooltip_btn>
            </Box>
          </Box>

          {/* Inline preview panel — collapsible */}
          {previewOpen && filePreviewUrl && (
            <Box sx={{
              borderTop: `1px solid ${alpha(NEON3, 0.15)}`,
              background: alpha(SURFACE_MID, 0.5),
            }}>
              {/* Preview toolbar */}
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2, py: 1,
                borderBottom: `1px solid ${alpha(NEON, 0.08)}`,
              }}>
                <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.6), letterSpacing: 0.5 }}>
                  PREVIEW
                </Typography>
                <Button
                  size="small"
                  href={filePreviewUrl} target="_blank" rel="noopener noreferrer"
                  startIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                  sx={{
                    px: 1.25, py: 0.3, borderRadius: '6px',
                    fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.72rem',
                    border: `1px solid ${alpha(NEON, 0.3)}`, color: NEON,
                    '&:hover': { background: alpha(NEON, 0.08) },
                  }}
                >
                  Open in tab
                </Button>
              </Box>
              <Box sx={{ height: { xs: 360, md: 500 } }}>
                <iframe
                  src={buildPdfEmbedSrc(filePreviewUrl)}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="PDF Preview"
                />
              </Box>
            </Box>
          )}
        </Box>

        {/* Preview toggle hint when closed */}
        {!previewOpen && (
          <Box
            onClick={() => setPreviewOpen(true)}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
              py: 1, borderRadius: '8px', cursor: 'pointer',
              border: `1px dashed ${alpha(NEON, 0.15)}`,
              background: alpha(SURFACE_UP, 0.2),
              transition: 'all 0.2s ease',
              '&:hover': { background: alpha(NEON, 0.04), borderColor: alpha(NEON, 0.3) },
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: 14, color: alpha(TEXT_MUTED, 0.5) }} />
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', color: alpha(TEXT_MUTED, 0.5) }}>
              Show preview
            </Typography>
          </Box>
        )}

        <input ref={fileInputRef} type="file" accept=".pdf" onChange={onFileChange} style={{ display: 'none' }} />
      </Box>
    );
  }

  // ── State: edit mode, existing file (no new upload yet) ─────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Existing file card */}
      <Box sx={{
        borderRadius: '12px', overflow: 'hidden',
        border: `1px solid ${alpha(NEON2, 0.25)}`,
        background: `linear-gradient(135deg, ${alpha(NEON2, 0.04)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
          <Box sx={{
            flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 48, height: 48, borderRadius: '10px',
            background: `linear-gradient(135deg, ${alpha('#F87171', 0.18)}, ${alpha('#F87171', 0.05)})`,
            border: `1px solid ${alpha('#F87171', 0.25)}`,
          }}>
            <PdfIcon sx={{ fontSize: 22, color: '#F87171' }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
              fontSize: '0.88rem', color: TEXT_BODY, mb: 0.3,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {documentData.document_name}.pdf
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                px: 1, py: 0.2, borderRadius: '4px',
                background: alpha(NEON2, 0.1), border: `1px solid ${alpha(NEON2, 0.25)}`,
              }}>
                <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6rem', color: NEON2, fontWeight: 600, letterSpacing: 0.5 }}>
                  CURRENT
                </Typography>
              </Box>
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem', color: alpha(TEXT_MUTED, 0.6) }}>
                Saved on server
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            <Tooltip_btn label="Preview current" onClick={() => setPreviewOpen(v => !v)}>
              {previewOpen ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
            </Tooltip_btn>
          </Box>
        </Box>

        {/* Inline preview for existing */}
        {previewOpen && filePreviewUrl && (
          <Box sx={{ borderTop: `1px solid ${alpha(NEON2, 0.15)}`, background: alpha(SURFACE_MID, 0.5) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: `1px solid ${alpha(NEON, 0.08)}` }}>
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.6), letterSpacing: 0.5 }}>
                CURRENT FILE PREVIEW
              </Typography>
              <Button size="small" href={filePreviewUrl} target="_blank" rel="noopener noreferrer"
                startIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                sx={{ px: 1.25, py: 0.3, borderRadius: '6px', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.72rem', border: `1px solid ${alpha(NEON, 0.3)}`, color: NEON, '&:hover': { background: alpha(NEON, 0.08) } }}>
                Open in tab
              </Button>
            </Box>
            <Box sx={{ height: { xs: 360, md: 500 } }}>
              <iframe src={buildPdfEmbedSrc(filePreviewUrl)} style={{ width: '100%', height: '100%', border: 'none' }} title="Current PDF" />
            </Box>
          </Box>
        )}
      </Box>

      {/* Replace prompt */}
      <Box
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
          p: 2, borderRadius: '10px', cursor: 'pointer',
          border: `1.5px dashed ${isDragOver ? alpha(NEON, 0.7) : alpha(NEON, 0.2)}`,
          background: isDragOver ? alpha(NEON, 0.06) : alpha(SURFACE_UP, 0.25),
          transition: 'all 0.2s ease',
          '&:hover': { background: alpha(NEON, 0.04), borderColor: alpha(NEON, 0.4) },
        }}
      >
        <ReplaceIcon sx={{ fontSize: 18, color: alpha(NEON, isDragOver ? 0.9 : 0.5) }} />
        <Box>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem', color: alpha(TEXT_BODY, 0.7) }}>
            Replace with a new PDF
          </Typography>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: alpha(TEXT_MUTED, 0.55) }}>
            Click to browse or drag & drop
          </Typography>
        </Box>
      </Box>

      <input ref={fileInputRef} type="file" accept=".pdf" onChange={onFileChange} style={{ display: 'none' }} />
    </Box>
  );
};

// ─── Small icon button helper ──────────────────────────────────────────────────
const Tooltip_btn = ({ label, onClick, color, children }) => (
  <Box
    title={label}
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 30, height: 30, borderRadius: '7px', cursor: 'pointer',
      color: color ?? alpha(TEXT_MUTED, 0.7),
      border: `1px solid ${alpha(color ?? NEON, 0.18)}`,
      background: 'transparent',
      transition: 'all 0.18s ease',
      '&:hover': {
        background: alpha(color ?? NEON, 0.1),
        color: color ?? NEON,
        borderColor: alpha(color ?? NEON, 0.4),
      },
    }}
  >
    {children}
  </Box>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const DocumentForm = () => {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const location  = useLocation();
  const { user }  = useAuth();

  const fileUploadedRef = useRef(false);

  const [loading,                 setLoading]                 = useState(false);
  const [dataLoading,             setDataLoading]             = useState(true);
  const [error,                   setError]                   = useState(null);
  const [successMessage,          setSuccessMessage]          = useState('');
  const [documentData,            setDocumentData]            = useState(location.state?.document);
  const [validationErrors,        setValidationErrors]        = useState({});
  const [showFieldErrors,         setShowFieldErrors]         = useState(false);
  const [events,                  setEvents]                  = useState([]);
  const [users,                   setUsers]                   = useState([]);
  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] = useState(false);
  const [pendingUpdateRequest,    setPendingUpdateRequest]    = useState(null);
  const [filePreviewUrl,          setFilePreviewUrl]          = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    event_id: '', document_name: '', document_pdf: null, publicity: '', user_ids: [],
  });

  const canManage  = canManageDocuments(user);
  const isEditMode = Boolean(documentData || id);

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'document',
    userId: user?.id,
    isEditMode,
    enabled: !dataLoading,
    formData,
    setFormData,
  });

  const eventOptions = useMemo(
    () => [
      ...events.map((event) => ({
        value: event.id,
        label: `${event.name} (${format(new Date(event.start_date), 'MMM dd, yyyy')})`,
      })),
    ],
    [events]
  );
  
  const publicityOptions = [
    { value: 'public',  label: 'Public — Visible to all team members' },
    { value: 'private', label: 'Private — Accessible to Team Leader and Secretary only' },
    { value: 'specific', label: 'Specific members — Visible only to selected members and document managers' },
  ];

  const userOptions = useMemo(
    () => users.map((member) => ({ value: String(member.id), label: member.name || member.email })),
    [users],
  );

  useEffect(() => {
    const cssId = 'documentform-ds-css';
    if (!window.document.getElementById(cssId)) {
      const el = window.document.createElement('style');
      el.id = cssId; el.innerHTML = FORM_CSS;
      window.document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    if (user && !canManage) navigate('/documents', { state: { error: 'You do not have permission to manage documents.' } });
  }, [user, canManage, navigate]);

  useEffect(() => {
    if (!user || !canManage) return;
    const fetchData = async () => {
      try {
        setDataLoading(true);
        if (id && !documentData) {
          const res  = await axiosInstance.get(`/documents/${id}`);
          const data = res.data?.data || res.data;
          setDocumentData(data);
          setFormData({ event_id: data.event_id || '', document_name: data.document_name || '', document_pdf: null, publicity: data.publicity || '', user_ids: (data.members || []).map((member) => String(member.id)) });
        }
        const [evRes, usersRes] = await Promise.all([
          axiosInstance.get('/documents/events/list'),
          axiosInstance.get('/users?active_only=1'),
        ]);
        setEvents(evRes.data?.data || evRes.data || []);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        setError(null);
      } catch { setError('Failed to fetch data'); }
      finally   { setDataLoading(false); }
    };
    fetchData();
  }, [user, canManage, id, documentData]);

  useEffect(() => {
    if (fileUploadedRef.current) return;
    if (documentData) {
      setFormData({ event_id: documentData.event_id || '', document_name: documentData.document_name || '', document_pdf: null, publicity: documentData.publicity || '', user_ids: (documentData.members || []).map((member) => String(member.id)) });
    } else if (!id) {
      setFormData({ event_id: '', document_name: '', document_pdf: null, publicity: '', user_ids: [] });
    }
  }, [documentData, id]);

  useEffect(() => {
    let revokeUrl = null;
    let cancelled = false;

    if (formData.document_pdf) {
      revokeUrl = URL.createObjectURL(formData.document_pdf);
      setFilePreviewUrl(revokeUrl);
    } else if (isEditMode && documentData?.document_pdf && id) {
      fetchDocumentPreviewBlob(id)
        .then((preview) => {
          if (cancelled) {
            URL.revokeObjectURL(preview.url);
            return;
          }
          revokeUrl = preview.url;
          setFilePreviewUrl(preview.url);
        })
        .catch(() => { if (!cancelled) setFilePreviewUrl(null); });
    } else {
      setFilePreviewUrl(null);
    }

    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [formData.document_pdf, documentData?.document_pdf, documentData?.preview_url, isEditMode, id]);

  const collectValidationErrors = () => {
    const errors = {};
    if (!formData.document_name.trim()) errors.document_name = 'Document name is required';
    if (!formData.publicity) errors.publicity = 'Publicity level is required';
    if (formData.publicity === 'specific' && !formData.user_ids.length) errors.user_ids = 'Select at least one member';
    if (!isEditMode && !formData.document_pdf) errors.document_pdf = 'PDF document is required';
    return errors;
  };

  const validateForm = () => {
    const errors = collectValidationErrors();
    setValidationErrors(errors);
    setShowFieldErrors(true);
    if (Object.keys(errors).length > 0) {
      setError(formatFieldErrorsMessage(errors));
      return false;
    }
    setError(null);
    return true;
  };

  const handleApiError = (err, fallbackMessage) => {
    const data = err?.response?.data;
    applyApiValidationErrors(data, setValidationErrors, DOCUMENT_VALIDATION_FIELDS);
    const fieldMsgs = {};
    const errs = data?.errors;
    if (errs && typeof errs === 'object') {
      Object.entries(errs).forEach(([key, msgs]) => {
        if (!DOCUMENT_VALIDATION_FIELDS.has(key)) return;
        const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
        if (msg) fieldMsgs[key] = String(msg);
      });
    }
    if (Object.keys(fieldMsgs).length) setShowFieldErrors(true);
    setError(
      Object.keys(fieldMsgs).length
        ? formatFieldErrorsMessage(fieldMsgs)
        : (getFirstErrorMessage(data) || fallbackMessage),
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const processSelectedFile = (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { setError('Please select a PDF file only'); return; }
    if (file.size > 20 * 1024 * 1024)   { setError('PDF file must be less than 20MB'); return; }
    fileUploadedRef.current = true;
    setFormData(prev => ({ ...prev, document_pdf: file }));
    setError(null);
    if (showFieldErrors && validationErrors.document_pdf) {
      setValidationErrors(prev => ({ ...prev, document_pdf: '' }));
    }
  };

  const handleFileChange = (e) => processSelectedFile(e.target.files[0]);
  const handleFileDrop   = (e) => { e.preventDefault(); e.stopPropagation(); processSelectedFile(e.dataTransfer?.files?.[0]); };
  const handleRemoveFile = () => { fileUploadedRef.current = false; setFormData(prev => ({ ...prev, document_pdf: null })); setFilePreviewUrl(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      if (documentData || id) {
        setError(null);
        setValidationErrors({});
        setShowFieldErrors(false);
        const submitData = new FormData();
        Object.keys(formData).forEach(key => {
          if (key === 'user_ids') formData.user_ids.forEach((userId) => submitData.append('user_ids[]', userId));
          if (key === 'document_pdf' && formData[key]) submitData.append(key, formData[key]);
          else if (key !== 'document_pdf' && key !== 'user_ids') submitData.append(key, formData[key]);
        });
        setPendingUpdateRequest({ url: `/documents/${documentData?.id || id}`, data: submitData });
        setConfirmUpdateDialogOpen(true);
        return;
      }

      if (!validateForm()) return;

      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'user_ids') formData.user_ids.forEach((userId) => submitData.append('user_ids[]', userId));
        if (key === 'document_pdf' && formData[key]) submitData.append(key, formData[key]);
        else if (key !== 'document_pdf' && key !== 'user_ids') submitData.append(key, formData[key]);
      });

      setLoading(true);
      await axiosInstance.post('/documents', submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!isEditMode) formDraft.clearDraft();
      setSuccessMessage('Document created successfully!');
      setTimeout(() => navigate('/documents'), 1500);
    } catch (err) {
      handleApiError(err, 'Failed to save document');
    } finally { setLoading(false); }
  };

  const confirmUpdate = async () => {
    const errors = collectValidationErrors();
    if (Object.keys(errors).length > 0) {
      rejectConfirmValidation({
        errors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen,
        setPendingUpdateRequest,
        setShowFieldErrors,
      });
      return;
    }
    try {
      setLoading(true);
      await axiosInstance.post(pendingUpdateRequest.url, pendingUpdateRequest.data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccessMessage('Document updated successfully!');
      setTimeout(() => navigate('/documents'), 1500);
    } catch (err) { handleApiError(err, 'Failed to update document'); }
    finally { setLoading(false); setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }
  };

  if (!canManage) return null;
  if (dataLoading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading document form" size={56} variant="primary" />
    </Box>
  );

  const canSubmit = Boolean(
    formData.document_name.trim() &&
    formData.publicity &&
    (formData.publicity !== 'specific' || formData.user_ids.length) &&
    (isEditMode || formData.document_pdf),
  );
  const { fieldHelper, fieldHasError } = createFieldErrorGate(showFieldErrors, validationErrors);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title={isEditMode ? 'Edit Document' : 'Upload New Document'}
        subtitle={isEditMode ? 'Update your document information and settings' : 'Add a new document to your activity library'}
        icon={DocumentIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
        backButtonText="Back to Documents"
      />

      <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage('')}
        sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95) }}
      />
      <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
        sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a',0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
      />

      {formDraft.showNotice && (
        <FormDraftNotice
          savedAt={formDraft.restoredAt}
          onDiscard={formDraft.discardDraft}
          onDismiss={formDraft.dismissNotice}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* ── Document Information ── */}
          <SectionCard title="Document Information" icon={<DocumentIcon />} delay={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <StyledField
                fullWidth required label="Document Name" name="document_name" value={formData.document_name}
                onChange={handleChange}
                error={fieldHasError('document_name')}
                helperText={fieldHelper('document_name')}
                InputProps={{ startAdornment: <InputAdornment position="start"><TitleIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
              />
              <SearchableSelect
                label="Related Event"
                value={formData.event_id}
                onChange={(v) => setFormData((prev) => ({ ...prev, event_id: v }))}
                options={eventOptions}
                startIcon={<EventIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
              />
              <SearchableSelect
                required
                label="Publicity Level"
                value={formData.publicity}
                onChange={(v) => {
                  setFormData((prev) => ({ ...prev, publicity: v }));
                  if (error) setError(null);
                  if (showFieldErrors && validationErrors.publicity) {
                    setValidationErrors((prev) => ({ ...prev, publicity: '' }));
                  }
                }}
                options={publicityOptions}
                error={fieldHasError('publicity')}
                helperText={fieldHelper('publicity')}
                startIcon={<LockIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
              />
              {formData.publicity === 'specific' && (
                <SearchableSelect
                  multiple
                  required
                  label="Members with access"
                  value={formData.user_ids}
                  onChange={(v) => setFormData((prev) => ({ ...prev, user_ids: v }))}
                  options={userOptions}
                  error={fieldHasError('user_ids')}
                  helperText={fieldHelper('user_ids')}
                  startIcon={<PeopleIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                  stackedTags
                />
              )}
            </Box>
          </SectionCard>

          {/* ── Document Upload ── */}
          <SectionCard title="Document Upload" icon={<PdfIcon />} delay={0.12}>
            <DocumentUploadZone
              formData={formData}
              filePreviewUrl={filePreviewUrl}
              isEditMode={isEditMode}
              documentData={documentData}
              documentPdfError={fieldHelper('document_pdf')}
              fileInputRef={fileInputRef}
              onFileChange={handleFileChange}
              onFileDrop={handleFileDrop}
              onRemoveFile={handleRemoveFile}
            />
          </SectionCard>

          <SubmitActionsCard
            statusText={isEditMode ? 'Save changes to update this document.' : 'Complete the form to upload a new document.'}
            missingRequiredFields={isEditMode ? null : [!formData.document_name.trim() && 'Document Name', !formData.publicity && 'Publicity', !formData.document_pdf && 'Document PDF'].filter(Boolean)}
            readyStatusText="All required fields are complete. Ready to upload document."
            cancelLabel="Cancel"
            onCancel={() => navigate(-1)}
            cancelDisabled={loading}
            submitLabel={isEditMode ? 'Update Document' : 'Upload Document'}
            submitDisabled={loading || !canSubmit}
            loading={loading}
            submitStartIcon={<SaveIcon />}
            {...submitDraftProps}
          />
        </Box>
      </form>

      <ConfirmUpdateDialog
        open={confirmUpdateDialogOpen}
        onClose={() => { setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }}
        onConfirm={confirmUpdate}
        title="Confirm Update"
        variant='edit'
        itemType='document'
        itemName={formData.document_name}
        description="This action will update the document details and cannot be undone."
        confirmLabel={isEditMode ? 'Update Document' : 'Upload Document'}
        confirmDisabled={!canSubmit}
        loading={loading}
      />
    </Container>
  );
};

export default DocumentForm;
